// src/pages/Invoices/InvoiceTypesPage.jsx — أنواع الفواتير (بالتصميم الموحّد، بدون حذف)
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, TextField, Button, IconButton, Tooltip, CircularProgress, InputAdornment,
  Dialog, DialogContent, DialogActions, ToggleButton, ToggleButtonGroup, Alert,
  Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import SouthWestIcon from '@mui/icons-material/SouthWest';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import { Switch, FormControlLabel } from '@mui/material';
import DynamicFormOutlinedIcon from '@mui/icons-material/DynamicFormOutlined';
import { toast } from 'react-toastify';
import InvoiceFieldsDialog from './InvoiceFieldsDialog';
import axios from '../../axios';
import { ReportPage, fieldSx, fmtNumber, BRAND } from '../../components/Reports/ReportKit';

const DIR = {
  1: { label: 'إدخال', hint: 'يزيد المخزون (مشتريات، مرتجع مبيعات...)', color: '#047857', bg: '#d1fae5', icon: <SouthWestIcon /> },
  2: { label: 'إخراج', hint: 'ينقص المخزون (مبيعات، مرتجع مشتريات...)', color: '#b45309', bg: '#fef3c7', icon: <NorthEastIcon /> },
};
const flag = (v) => v !== false; // الافتراضي: يؤثر
const Effect = ({ on, icon, label }) => (
  <Tooltip title={on ? `يؤثر على ${label}` : `لا يؤثر على ${label}`}>
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: .4, px: .9, py: .2, borderRadius: '999px', fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap',
      color: on ? '#4338ca' : '#94a3b8', bgcolor: on ? '#eef2ff' : '#f1f5f9', textDecoration: on ? 'none' : 'line-through', '& svg': { fontSize: 14 } }}>
      {icon}{label}
    </Box>
  </Tooltip>
);
const errMsg = (e, fb) => {
  const d = e?.response?.data;
  return d?.message || (typeof d === 'string' ? d : d?.title) || fb;
};

const Chip = ({ children, color, bg, icon }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: .4, px: 1, py: .2, borderRadius: '999px', fontSize: 12, fontWeight: 800, color, bgcolor: bg, whiteSpace: 'nowrap', '& svg': { fontSize: 14 } }}>
    {icon}{children}
  </Box>
);

export default function InvoiceTypesPage() {
  const companyId = parseInt(localStorage.getItem('companyId'), 10);
  const [types, setTypes] = useState([]);
  const [usage, setUsage] = useState({ start: 1000, counts: {} });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState(null); // null | { mode:'add' } | { mode:'edit', item }
  const [fieldsOf, setFieldsOf] = useState(null);       // نوع الفاتورة المفتوحة حقوله
  const [fieldCounts, setFieldCounts] = useState({});   // invoiceTypeId -> عدد الحقول المخصصة

  const loadFieldCounts = useCallback(() => {
    if (!companyId) return;
    axios.get('/InvoiceFields/counts', { params: { companyId } })
      .then(r => setFieldCounts(r.data || {}))
      .catch(() => {});
  }, [companyId]);

  useEffect(() => { loadFieldCounts(); }, [loadFieldCounts]);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const [res, u] = await Promise.all([
        axios.get(`/InvoiceTypes?companyId=${companyId}`),
        axios.get(`/InvoiceTypes/usage?companyId=${companyId}`).catch(() => null),
      ]);
      setTypes((Array.isArray(res.data) ? res.data : []).filter(t => !(t.is_deleted ?? t.isDeleted)));
      if (u?.data) {
        setUsage({
          start: u.data.customTypesStart ?? 1000,
          counts: Object.fromEntries((u.data.counts || []).map(c => [c.invoiceTypeId, c.count])),
        });
      }
    } catch (e) {
      toast.error(errMsg(e, 'تعذر تحميل أنواع الفواتير'));
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const isSystem = (t) => Number(t.invoiceTypeId) < usage.start;
  const countOf = (t) => usage.counts[t.invoiceTypeId] || 0;

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? types.filter(t => String(t.name).toLowerCase().includes(q) || String(t.invoiceTypeId).includes(q)) : types;
  }, [types, search]);

  const stats = [
    { label: 'إجمالي الأنواع', value: fmtNumber(types.length), icon: <CategoryOutlinedIcon />, color: 'primary', highlight: true },
    { label: 'إدخال', value: fmtNumber(types.filter(t => Number(t.direction) === 1).length), icon: <SouthWestIcon />, color: 'success' },
    { label: 'إخراج', value: fmtNumber(types.filter(t => Number(t.direction) === 2).length), icon: <NorthEastIcon />, color: 'warning' },
    { label: 'أنواع مضافة', value: fmtNumber(types.filter(t => !isSystem(t)).length), icon: <TuneIcon />, color: 'info' },
  ];

  const th = { fontWeight: 800, color: '#475569', bgcolor: '#f8fafc', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' };

  return (
    <ReportPage
      title="أنواع الفواتير"
      subtitle="أنواع الفواتير واتجاه أثرها على المخزون"
      icon={<CategoryOutlinedIcon />}
      actions={[{ label: 'إضافة نوع', icon: <AddIcon />, onClick: () => setDialog({ mode: 'add' }), primary: true }]}
      stats={types.length ? stats : []}
    >
      <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', p: 1.25, borderBottom: '1px solid #e5e7eb', bgcolor: '#fafbfc' }}>
          <TextField size="small" value={search} onChange={e => setSearch(e.target.value)} placeholder="بحث بالاسم أو الرقم..."
            sx={{ ...fieldSx, flex: 1, maxWidth: 420 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment> }} />
          <Box sx={{ flex: 1 }} />
          <Tooltip title="تحديث"><span>
            <IconButton onClick={load} disabled={loading} sx={{ border: '1px solid #e5e7eb', borderRadius: '10px' }}><RefreshIcon /></IconButton>
          </span></Tooltip>
        </Box>

        {loading ? (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}><CircularProgress sx={{ color: BRAND.primary }} /></Box>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ '& td, & th': { py: 1.1, textAlign: 'right' } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...th, width: 80 }}>الرقم</TableCell>
                  <TableCell sx={th}>الاسم</TableCell>
                  <TableCell sx={{ ...th, width: 130 }}>الاتجاه</TableCell>
                  <TableCell sx={{ ...th, width: 190 }}>الأثر</TableCell>
                  <TableCell sx={{ ...th, width: 110 }}>التصنيف</TableCell>
                  <TableCell sx={{ ...th, width: 110 }}>الفواتير</TableCell>
                  <TableCell sx={{ ...th, width: 70 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {shown.map(t => {
                  const d = DIR[Number(t.direction)] || DIR[2];
                  const sys = isSystem(t);
                  return (
                    <TableRow key={t.id} hover sx={{ '&:hover .edit-btn': { opacity: 1 } }}>
                      <TableCell sx={{ fontFamily: 'monospace', fontWeight: 800, color: BRAND.primary }}>{t.invoiceTypeId}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{t.name}</TableCell>
                      <TableCell><Chip color={d.color} bg={d.bg} icon={d.icon}>{d.label}</Chip></TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: .5 }}>
                          <Effect on={flag(t.affectsStock)} icon={<Inventory2OutlinedIcon />} label="المخزون" />
                          <Effect on={flag(t.affectsAccounting)} icon={<AccountBalanceOutlinedIcon />} label="المحاسبة" />
                        </Box>
                      </TableCell>
                      <TableCell>
                        {sys
                          ? <Chip color="#475569" bg="#f1f5f9" icon={<LockOutlinedIcon />}>نظام</Chip>
                          : <Chip color={BRAND.primary} bg={BRAND.primarySoft}>مضاف</Chip>}
                      </TableCell>
                      <TableCell sx={{ fontVariantNumeric: 'tabular-nums', color: countOf(t) ? '#0f172a' : '#94a3b8', fontWeight: 700 }}>{fmtNumber(countOf(t))}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: .5, alignItems: 'center' }}>
                          <Tooltip title="الحقول المخصصة">
                            <IconButton size="small" onClick={() => setFieldsOf(t)}
                              sx={{ color: fieldCounts[t.invoiceTypeId] ? BRAND.primary : '#94a3b8', gap: .25, borderRadius: '8px' }}>
                              <DynamicFormOutlinedIcon fontSize="small" />
                              {fieldCounts[t.invoiceTypeId] > 0 && (
                                <Box component="span" sx={{ fontSize: 12, fontWeight: 800 }}>{fieldCounts[t.invoiceTypeId]}</Box>
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="تعديل">
                            <IconButton className="edit-btn" size="small" onClick={() => setDialog({ mode: 'edit', item: t })} sx={{ color: '#d97706', opacity: { xs: 1, md: .55 } }}>
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!shown.length && (
                  <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center !important', py: 6, color: '#94a3b8' }}>لا توجد أنواع</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        )}
      </Paper>

      <Typography sx={{ fontSize: 12.5, color: '#94a3b8', mt: 1.25, display: 'flex', alignItems: 'center', gap: .5 }}>
        <LockOutlinedIcon sx={{ fontSize: 15 }} /> لا يمكن حذف أنواع الفواتير لارتباطها بالفواتير والترقيم والتقارير — يمكن تعديل الاسم دائمًا، والأثر المخزني/المحاسبي لأي نوع ليس عليه فواتير، والاتجاه للأنواع المضافة فقط.
      </Typography>

      {fieldsOf && (
        <InvoiceFieldsDialog
          companyId={companyId}
          type={fieldsOf}
          onClose={() => setFieldsOf(null)}
          onChanged={loadFieldCounts}
        />
      )}

      {dialog && (
        <TypeDialog
          dialog={dialog}
          companyId={companyId}
          dirLocked={dialog.item ? isSystem(dialog.item) || countOf(dialog.item) > 0 : false}
          locked={dialog.item ? countOf(dialog.item) > 0 || Number(dialog.item.invoiceTypeId) === 10 : false}
          lockReason={dialog.item ? (countOf(dialog.item) > 0 ? `عليه ${countOf(dialog.item)} فاتورة` : Number(dialog.item.invoiceTypeId) === 10 ? 'عرض السعر مستند فقط' : isSystem(dialog.item) ? 'اتجاه نوع نظام' : '') : ''}
          onClose={() => setDialog(null)}
          onSaved={() => { setDialog(null); load(); }}
        />
      )}
    </ReportPage>
  );
}

function TypeDialog({ dialog, companyId, locked, dirLocked, lockReason, onClose, onSaved }) {
  const edit = dialog.mode === 'edit';
  const [name, setName] = useState(edit ? dialog.item.name : '');
  const [direction, setDirection] = useState(edit ? Number(dialog.item.direction) : 2);
  const [affectsStock, setAffectsStock] = useState(edit ? flag(dialog.item.affectsStock) : true);
  const [affectsAccounting, setAffectsAccounting] = useState(edit ? flag(dialog.item.affectsAccounting) : true);
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) { toast.error('الرجاء إدخال اسم النوع'); return; }
    setSaving(true);
    try {
      const body = { name: name.trim(), direction, affectsStock, affectsAccounting };
      if (edit) await axios.put(`/InvoiceTypes/${dialog.item.id}?companyId=${companyId}`, body);
      else await axios.post(`/InvoiceTypes?companyId=${companyId}`, body);
      toast.success(edit ? 'تم تعديل النوع' : 'تمت إضافة النوع');
      onSaved();
    } catch (err) {
      toast.error(errMsg(err, 'تعذر الحفظ'));
    } finally {
      setSaving(false);
    }
  };

  const tbSx = {
    '& .MuiToggleButton-root': { flex: 1, py: 1, fontWeight: 800, gap: .75, borderRadius: '10px !important', border: '1px solid #e5e7eb !important', mx: .25 },
    '& .Mui-selected': { bgcolor: `${BRAND.primarySoft} !important`, color: `${BRAND.primary} !important`, borderColor: `${BRAND.primaryLight} !important` },
  };

  return (
    <Dialog open onClose={() => !saving && onClose()} dir="rtl" maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '18px', overflow: 'hidden' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1.5, color: '#fff', background: BRAND.headerGradient }}>
        <Box sx={{ width: 36, height: 36, borderRadius: '10px', display: 'grid', placeItems: 'center', bgcolor: 'rgba(255,255,255,.16)', border: '1px solid rgba(255,255,255,.28)' }}>
          {edit ? <EditOutlinedIcon fontSize="small" /> : <AddIcon fontSize="small" />}
        </Box>
        <Typography sx={{ flex: 1, fontWeight: 800, fontSize: 16 }}>{edit ? `تعديل نوع (${dialog.item.invoiceTypeId})` : 'إضافة نوع فاتورة'}</Typography>
        <IconButton onClick={onClose} disabled={saving} size="small" sx={{ color: '#fff' }}><CloseIcon /></IconButton>
      </Box>
      <DialogContent component="form" onSubmit={save} sx={{ display: 'grid', gap: 2, pt: '20px !important' }}>
        <TextField size="small" label="اسم النوع *" value={name} onChange={e => setName(e.target.value)} autoFocus sx={fieldSx} />
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#475569', mb: .75 }}>الاتجاه (أثره على المخزون)</Typography>
          <ToggleButtonGroup exclusive fullWidth value={direction} onChange={(_, v) => v && setDirection(v)} disabled={dirLocked} sx={tbSx}>
            {[1, 2].map(v => <ToggleButton key={v} value={v}>{DIR[v].icon}{DIR[v].label}</ToggleButton>)}
          </ToggleButtonGroup>
          <Typography sx={{ fontSize: 12, color: '#64748b', mt: .5 }}>{DIR[direction]?.hint}</Typography>
        </Box>
        <Box sx={{ display: 'grid', gap: 1 }}>
          {[
            [affectsStock, setAffectsStock, <Inventory2OutlinedIcon />, 'التأثير المخزني', 'يحرّك كميات المخزون عند الاعتماد'],
            [affectsAccounting, setAffectsAccounting, <AccountBalanceOutlinedIcon />, 'التأثير المحاسبي', 'ينشئ القيد ويحرّك الخزينة/البنك وحساب العميل أو المورد'],
          ].map(([v, setV, icon, l, hint]) => (
            <Box key={l} sx={{ p: 1.1, borderRadius: '12px', border: `1px solid ${v ? BRAND.primaryLight : '#e5e7eb'}`, bgcolor: v ? '#fafaff' : '#fff', opacity: locked ? .7 : 1 }}>
              <FormControlLabel disabled={locked} sx={{ m: 0, width: '100%', justifyContent: 'space-between', flexDirection: 'row-reverse' }}
                control={<Switch checked={v} onChange={e => setV(e.target.checked)} />}
                label={<Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Box sx={{ color: v ? BRAND.primary : '#94a3b8', display: 'flex' }}>{icon}</Box>
                  <Box><Typography sx={{ fontWeight: 800, fontSize: 14 }}>{l}</Typography><Typography sx={{ fontSize: 12, color: '#64748b' }}>{hint}</Typography></Box>
                </Box>} />
            </Box>
          ))}
          {!affectsStock && !affectsAccounting && (
            <Typography sx={{ fontSize: 12, color: '#b45309' }}>نوع بلا أثر (مثل عرض السعر أو أمر البيع) — مستند فقط.</Typography>
          )}
        </Box>
        {(locked || dirLocked) && (
          <Alert severity="info" icon={<LockOutlinedIcon fontSize="small" />} sx={{ borderRadius: '12px', py: 0 }}>
            {locked ? 'الاتجاه والأثر مقفلان' : 'الاتجاه مقفل'} ({lockReason}){locked ? ' — تغييرهما يجعل الفواتير السابقة غير متسقة.' : ''}
          </Alert>
        )}
        <button type="submit" hidden />
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: '1px solid #eef0f4' }}>
        <Button variant="text" onClick={onClose} disabled={saving} sx={{ fontWeight: 700, color: '#64748b', bgcolor: 'transparent', '&:hover': { bgcolor: '#f1f5f9' } }}>إلغاء</Button>
        <Button variant="contained" onClick={save} disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveOutlinedIcon />}
          sx={{ borderRadius: '10px', fontWeight: 800, px: 3, background: BRAND.buttonGradient, '& .MuiButton-startIcon': { ml: .75, mr: -.25 } }}>
          {saving ? 'جاري الحفظ...' : 'حفظ'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}